import React from 'react';

import { ScreenWithOptionalParams } from './ScreenWithOptionalParams.type';

interface ContextValue
  extends Readonly<{
    screens: ScreenWithOptionalParams[];
    selectedScreenIndex: number;
    selectedScreen: ScreenWithOptionalParams;
    select: (screenIndex: number) => void;
    reset: () => void;
  }> {}

const Context = React.createContext<ContextValue>({
  screens: [],
  selectedScreenIndex: -1,
  selectedScreen: { screen: '' },
  select: (_screenIndex: number) => {
    throw new Error('TabNavigationContextProvider not found!');
  },
  reset: () => {
    throw new Error('TabNavigationContextProvider not found!');
  },
});

interface TabNavigationContextProviderProps
  extends Readonly<{
    screens: ScreenWithOptionalParams[];
    initialSelectedScreenIndex: number;
    children?: React.ReactNode;
  }> {}

export const TabNavigationContextProvider = React.memo<TabNavigationContextProviderProps>((props) => {
  const { screens, initialSelectedScreenIndex, children } = props;

  const [selectedScreenIndex, setSelectedScreenIndex] = React.useState<number>(() => initialSelectedScreenIndex);

  const ResetCallback = React.useCallback(() => setSelectedScreenIndex(initialSelectedScreenIndex), [initialSelectedScreenIndex]);

  const SelectedScreen = React.useMemo<ScreenWithOptionalParams>(() => screens[selectedScreenIndex], [screens, selectedScreenIndex]);

  const ContextValue = React.useMemo(() => {
    const contextValue: ContextValue = {
      screens,
      selectedScreenIndex,
      selectedScreen: SelectedScreen,
      select: setSelectedScreenIndex,
      reset: ResetCallback,
    };

    return contextValue;
  }, [screens, selectedScreenIndex, SelectedScreen, setSelectedScreenIndex, ResetCallback]);

  return <Context.Provider value={ContextValue}>{children}</Context.Provider>;
});

export const useTabNavigationContext = () => React.useContext<ContextValue>(Context);
